import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Tag, Space, Typography, Alert, Table, Modal, Form, Input, InputNumber,
  Select, App as AntApp, Image, Descriptions, Segmented, DatePicker, Timeline, Switch, Checkbox,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, DeleteOutlined, UndoOutlined, DownloadOutlined } from '@ant-design/icons';
import { API_URL } from '../config';

const { Title, Text } = Typography;

interface CategoriaDre {
  linha_dre: string;
  grupo_dre: string;
  ordem: number;
}

type StatusReembolso =
  | 'aguardando_upload' | 'aguardando_ocr' | 'aguardando_revisao'
  | 'aprovado' | 'rejeitado' | 'duplicado' | 'erro_ocr';

interface ExtracaoOCR {
  valor: number | null;
  data_emissao: string | null;
  emissor: string | null;          // loja/estabelecimento que emitiu o documento
  emissor_cnpj: string | null;
  fornecedor: string | null;       // PESSOA que recebe o reembolso (nova regra contábil)
  fornecedor_id?: string | null;
  cnpj?: string | null;            // legado (antes da migração — era o CNPJ da loja)
  natureza_sugerida: string | null;
  descricao: string | null;
  tipo_documento: string | null;
  confianca: number | null;
  // A leitura automática falhou (foto pesada, PDF ilegível): o pedido chega à
  // fila só com o comprovante, e quem revisa preenche os campos.
  erro_leitura?: string;
  // Conferência da data feita no servidor contra a data da FOTO do comprovante
  // (mobile-engine `_conferirDataEmissao`). Null/ausente = data plausível.
  data_alerta?: {
    gap_dias: number;
    motivo: string;
    data_texto?: string | null;      // o que estava escrito no documento
    data_sugerida: string | null;    // só no caminho legado (sem texto do documento)
    sugestao_motivo: string | null;
    confirmado_por?: string | null;  // quem aprovou mesmo com o aviso de pé
    confirmado_em?: string | null;
  } | null;
  // Texto da data exatamente como escrito no documento (a conversão é nossa).
  data_emissao_texto?: string | null;
}

interface Reembolso {
  id: number;
  cognito_sub: string;
  email_usuario: string;
  contrato_id: string | null;
  centro_custo_codigo: string | null;
  centro_custo_nome?: string | null;
  contrato_numero?: string | null;
  contrato_titulo?: string | null;
  comprovante_s3_key: string;
  comprovante_content_type?: string | null;
  capturado_em: string;
  ai_extracao: ExtracaoOCR | null;
  status: StatusReembolso;
  revisado_por: string | null;
  revisado_em: string | null;
  motivo_rejeicao: string | null;
  sem_comprovante: boolean;
  justificativa_sem_comprovante: string | null;
  // A que o gasto se refere, na palavra de quem pediu (≠ ai_extracao.descricao,
  // que é a leitura do comprovante, e ≠ comentario_financeiro, de quem revisa).
  descricao_solicitante?: string | null;
  comentario_financeiro?: string | null;
  // Categoria SALVA (coluna própria; a listagem devolve) — vence o palpite da IA.
  categoria_dre?: string | null;
  // Suspeita de repetido (mesmo CNPJ + valor + data de outro pedido). Desde
  // 02/10/2026 o pedido NÃO sai da fila por isso — só chega marcado.
  duplicidade_de?: number | null;
  excluido_em: string | null;
  excluido_por: string | null;
  motivo_exclusao: string | null;
  created_at: string;
}

const STATUS_TAG: Record<StatusReembolso, { color: string; txt: string }> = {
  aguardando_upload:  { color: 'default', txt: 'Subindo foto' },
  aguardando_ocr:     { color: 'processing', txt: 'Processando IA' },
  aguardando_revisao: { color: 'warning', txt: 'Aguarda revisão' },
  aprovado:           { color: 'success', txt: 'Aprovado' },
  rejeitado:          { color: 'error', txt: 'Rejeitado' },
  duplicado:          { color: 'warning', txt: 'Preso: repetido?' },
  erro_ocr:           { color: 'error', txt: 'Preso: leitura falhou' },
};

const NATUREZA_PARA_CATEGORIA_DRE: Record<string, string | null> = {
  combustivel:       'Combustíveis',
  alimentacao:       'Alimentação em campo',
  hospedagem:        'Diárias / Hospedagem',
  transporte:        'Passagens Aéreas / Ônibus',
  material:          'Materiais Aplicados na Prestação de Serviços',
  servico_terceiro:  'Honorários Terceirizados',
  outros:            null,
};

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmtMoeda = (v: number | null | undefined) =>
  v == null ? '—' : 'R$ ' + Number(v).toFixed(2).replace('.', ',');

const chaveDestino = (r: Pick<Reembolso, 'contrato_id' | 'centro_custo_codigo'>) =>
  r.contrato_id ? `contrato:${r.contrato_id}`
    : r.centro_custo_codigo ? `centro:${r.centro_custo_codigo}` : undefined;

const payloadDestino = (chave?: string | null) => ({
  contrato_id: chave?.startsWith('contrato:') ? chave.slice(9) : null,
  centro_custo_codigo: chave?.startsWith('centro:') ? chave.slice(7) : null,
});

const ITENS_POR_PAGINA = 25;

function FinanceiroReembolsosCampoInner() {
  const { message } = AntApp.useApp();
  const [reembolsos, setReembolsos] = useState<Reembolso[]>([]);
  const [categorias, setCategorias] = useState<CategoriaDre[]>([]);
  // Contratos pro campo editável de vínculo: é do contrato que sai o centro de
  // custo do lançamento na aprovação, então sem ele o CP nasce sem CC.
  const [contratos, setContratos] = useState<{ id: string; numero: string | null; titulo: string | null }[]>([]);
  const [centrosCusto, setCentrosCusto] = useState<{ codigo_canonical: string; nome_completo: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<string>('aguardando_revisao');
  // Pedidos antigos presos em status que nenhuma tela mostrava (leitura falhou
  // / repetido). O filtro "Com problema" só aparece enquanto houver algum.
  const [comProblema, setComProblema] = useState(0);
  const [mostrarExcluidos, setMostrarExcluidos] = useState(false);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [pagina, setPagina] = useState(1);
  const [erro, setErro] = useState<string | null>(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [reembolsoSelecionado, setReembolsoSelecionado] = useState<Reembolso | null>(null);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  // PDF não renderiza em <img>: a tela precisa saber o tipo para escolher o visor.
  const [ehPdf, setEhPdf] = useState(false);
  const [fotoDownloadUrl, setFotoDownloadUrl] = useState<string | null>(null);
  const [eventos, setEventos] = useState<any[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [form] = Form.useForm();
  // A lista vem do servidor com limite de 100 itens; a busca precisa acontecer
  // antes desse limite para encontrar reembolsos mais antigos também.
  const listaRequestId = useRef(0);

  // Aviso da data: quem faz a conta é o SERVIDOR (uma régua só, em
  // `_conferirDataEmissao`); a tela só mostra e some quando a pessoa troca a data
  // do campo pela sugestão (ou por outra) — nada de reimplementar a regra aqui.
  const dataNoForm = Form.useWatch('data_emissao', form) as Dayjs | undefined;
  const alertaData = (() => {
    const a = reembolsoSelecionado?.ai_extracao?.data_alerta;
    if (!a) return null;
    const conferida = reembolsoSelecionado?.ai_extracao?.data_emissao || '';
    if (dataNoForm?.isValid?.() && dataNoForm.format('YYYY-MM-DD') !== conferida) return null;
    return a;
  })();

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro, mostrarExcluidos, buscaAplicada]);

  useEffect(() => {
    const atraso = window.setTimeout(() => setBuscaAplicada(busca.trim()), 300);
    return () => window.clearTimeout(atraso);
  }, [busca]);

  // Deep-link do sino: abre a revisão do reembolso específico quando a lista
  // carrega (uma vez). O aviso do revisor é sempre "aguardando_revisao" (filtro
  // padrão), então o item costuma já estar na lista.
  const deepLinkFeito = useRef(false);
  useEffect(() => {
    if (deepLinkFeito.current || !reembolsos.length) return;
    const alvo = sessionStorage.getItem('reembolsos_campo_abrir_id');
    if (!alvo) return;
    const r = reembolsos.find((x) => String(x.id) === alvo);
    if (!r) return; // pode estar em outro filtro — não consome a chave
    sessionStorage.removeItem('reembolsos_campo_abrir_id');
    deepLinkFeito.current = true;
    abrirRevisao(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reembolsos]);

  useEffect(() => {
    // Carrega categorias DRE do banco (1x)
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/mobile/categorias-dre`, {
          headers: { Authorization: token },
        });
        const raw: any = r.data;
        const data: any = raw?.body ? JSON.parse(raw.body) : raw;
        setCategorias(Array.isArray(data?.categorias) ? data.categorias : []);
      } catch { /* silencioso */ }
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/mobile/contratos`, {
          headers: { Authorization: token },
        });
        const raw: any = r.data;
        const data: any = raw?.body ? JSON.parse(raw.body) : raw;
        setContratos(Array.isArray(data?.contratos) ? data.contratos : []);
        setCentrosCusto(Array.isArray(data?.centros_custo) ? data.centros_custo : []);
      } catch { /* silencioso */ }
    })();
  }, []);

  async function carregar() {
    const requestId = ++listaRequestId.current;
    setLoading(true);
    setErro(null);
    try {
      const token = await getToken();
      axios.get(`${API_URL}/mobile/reembolsos/contagem-pendente`, { headers: { Authorization: token } })
        .then((c) => {
          const d: any = (c.data as any)?.body ? JSON.parse((c.data as any).body) : c.data;
          setComProblema(typeof d?.com_problema === 'number' ? d.com_problema : 0);
        })
        .catch(() => { /* o filtro extra é opcional */ });
      const params: Record<string, string> = {};
      if (filtro !== 'todos') params.status = filtro;
      if (mostrarExcluidos) params.incluir_excluidos = '1';
      if (buscaAplicada) params.q = buscaAplicada;
      const r = await axios.get<{ reembolsos: Reembolso[] }>(`${API_URL}/mobile/reembolsos`, {
        headers: { Authorization: token },
        params,
      });
      if (requestId === listaRequestId.current) {
        const lista = r.data.reembolsos || [];
        setReembolsos(lista);
        // Uma exclusão ou uma busca pode deixar a página atual sem linhas.
        // Mantém a página quando ainda existe e só volta à última disponível.
        setPagina((atual) => Math.min(atual, Math.max(1, Math.ceil(lista.length / ITENS_POR_PAGINA))));
      }
    } catch (e: any) {
      if (requestId === listaRequestId.current) setErro(e.response?.data?.error || e.message);
    } finally {
      if (requestId === listaRequestId.current) setLoading(false);
    }
  }

  async function excluir() {
    if (!reembolsoSelecionado) return;
    const r = reembolsoSelecionado;
    const isAprovado = r.status === 'aprovado';
    Modal.confirm({
      title: isAprovado ? '⚠️ Excluir reembolso APROVADO' : 'Excluir reembolso',
      width: 520,
      content: (
        <div>
          {isAprovado && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="Este reembolso já virou lançamento em Contas a Pagar"
              description="A exclusão vai apagar também o lançamento AP vinculado. Esta ação pode ser desfeita por admin."
            />
          )}
          <Text style={{ display: 'block', marginBottom: 4 }}>
            <strong>Motivo da exclusão *</strong>
          </Text>
          <Input.TextArea
            id="motivo-exclusao"
            placeholder="Ex: lançamento duplicado, contrato errado, valor incorreto..."
            rows={3}
          />
          {isAprovado && (
            <div style={{ marginTop: 12 }}>
              <Checkbox id="estornar-ap" defaultChecked>
                Estornar o lançamento em Contas a Pagar vinculado
              </Checkbox>
            </div>
          )}
        </div>
      ),
      okText: 'Excluir',
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      cancelText: 'Cancelar',
      onOk: async () => {
        const motivo = (document.getElementById('motivo-exclusao') as HTMLTextAreaElement)?.value?.trim() || '';
        const estornar = (document.getElementById('estornar-ap') as HTMLInputElement)?.checked ?? true;
        if (motivo.length < 5) {
          message.error('Motivo precisa de pelo menos 5 caracteres');
          throw new Error('motivo invalido');
        }
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/mobile/reembolsos/${r.id}`, {
            headers: { Authorization: token },
            data: { motivo, estornar_lancamento_pagar: estornar },
          });
          message.success('Reembolso excluído');
          setModalAberto(false);
          carregar();
        } catch (e: any) {
          message.error('Falha: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  async function desfazerExclusaoDre() {
    if (!reembolsoSelecionado) return;
    try {
      const token = await getToken();
      await axios.post(
        `${API_URL}/mobile/reembolsos/${reembolsoSelecionado.id}/desfazer-exclusao`,
        {},
        { headers: { Authorization: token } },
      );
      message.success('Exclusão desfeita');
      setModalAberto(false);
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  async function abrirRevisao(r: Reembolso) {
    setReembolsoSelecionado(r);
    setModalAberto(true);
    setFotoUrl(null);
    setEhPdf(/pdf/i.test(r.comprovante_content_type || '') || /\.pdf$/i.test(r.comprovante_s3_key || ''));
    setFotoDownloadUrl(null);
    const naturezaSug = r.ai_extracao?.natureza_sugerida ?? '';
    const dataIso = r.ai_extracao?.data_emissao ?? '';
    const dataDayjs = dataIso ? dayjs(dataIso) : null;
    form.setFieldsValue({
      valor: r.ai_extracao?.valor ?? null,
      data_emissao: dataDayjs && dataDayjs.isValid() ? dataDayjs : null,
      emissor: r.ai_extracao?.emissor ?? '',
      emissor_cnpj: r.ai_extracao?.emissor_cnpj ?? '',
      fornecedor: r.ai_extracao?.fornecedor ?? '',
      // O que foi SALVO vence o palpite da IA. Era daqui que vinha o "salvei a
      // categoria e ela voltou" (Daniele, 31/08; Lucas, 27/08): o servidor
      // gravava certo — no reembolso E no lançamento — mas a tela reabria
      // semeando da natureza sugerida pela IA, e o salvo ficava invisível.
      // Comentário e contrato sempre semearam do registro; só a categoria
      // tinha ficado no palpite.
      categoria_dre: r.categoria_dre || NATUREZA_PARA_CATEGORIA_DRE[naturezaSug] || '',
      // O texto do solicitante vale mais que a leitura do comprovante: a nota
      // diz a loja, ele diz a finalidade. Mesma ordem do backend na aprovação.
      descricao: r.descricao_solicitante || r.ai_extracao?.descricao || '',
      comentario_financeiro: r.comentario_financeiro ?? '',
      destino: chaveDestino(r),
    });

    try {
      const token = await getToken();
      const det = await axios.get<{ comprovante_url: string; comprovante_download_url?: string | null;
        reembolso?: { comprovante_content_type?: string | null; comprovante_s3_key?: string | null } }>(
        `${API_URL}/mobile/reembolsos/${r.id}`,
        { headers: { Authorization: token } },
      );
      // confirma o tipo pelo registro completo (a listagem pode não trazer o content_type)
      const ct = det.data.reembolso?.comprovante_content_type || '';
      const ky = det.data.reembolso?.comprovante_s3_key || '';
      if (ct || ky) setEhPdf(/pdf/i.test(ct) || /\.pdf$/i.test(ky));
      setFotoUrl(det.data.comprovante_url);
      setFotoDownloadUrl(det.data.comprovante_download_url || det.data.comprovante_url);

      // Busca histórico de eventos
      try {
        const ev = await axios.get(`${API_URL}/mobile/reembolsos/${r.id}/eventos`, {
          headers: { Authorization: token },
        });
        const evRaw: any = ev.data;
        const evData: any = evRaw?.body ? JSON.parse(evRaw.body) : evRaw;
        setEventos(Array.isArray(evData?.eventos) ? evData.eventos : []);
      } catch { setEventos([]); }
    } catch (e: any) {
      message.error('Falha ao carregar foto: ' + e.message);
    }
  }

  const EVENTO_LABEL: Record<string, { color: string; txt: string }> = {
    criado:                 { color: 'gray',  txt: 'Reembolso criado' },
    sem_comprovante_criado: { color: 'orange', txt: 'Lançado sem comprovante' },
    ocr_processado:         { color: 'blue',  txt: 'IA processou comprovante' },
    ocr_falhou:             { color: 'orange', txt: 'Leitura automática falhou — seguiu para revisão manual' },
    foto_refeita:           { color: 'blue',  txt: 'Foto refeita' },
    editado_manual:         { color: 'blue',  txt: 'Dados editados manualmente' },
    rejeitado:              { color: 'red',   txt: 'Rejeitado pelo financeiro' },
    aprovado:               { color: 'green', txt: 'Aprovado pelo financeiro' },
    editado_pos_aprovacao:  { color: 'blue',  txt: 'Editado após aprovação' },
    reenviado:              { color: 'orange', txt: 'Reenviado para revisão' },
    contrato_alterado:      { color: 'blue',  txt: 'Centro de custo alterado' },
  };

  async function aprovar() {
    if (!reembolsoSelecionado) return;
    // validateFields FORA do try deixava o botão mudo: com Valor/Data/Categoria
    // vazios a promise rejeitava sem catch e nada acontecia na tela — a revisora
    // clicava em "Aprovar" e não recebia resposta nenhuma (relato 19/08/2026).
    let vals: any;
    try { vals = await form.validateFields(); }
    catch { message.error('Preencha os campos obrigatórios (valor, data de emissão e categoria).'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const dataIso = vals.data_emissao && (vals.data_emissao as Dayjs).isValid?.()
        ? (vals.data_emissao as Dayjs).format('YYYY-MM-DD')
        : null;
      const r = await axios.post(
        `${API_URL}/mobile/reembolsos/${reembolsoSelecionado.id}/aprovar`,
        {
          valor: vals.valor,
          data_competencia: dataIso,
          data_emissao: dataIso,
          emissor: vals.emissor || null,
          emissor_cnpj: vals.emissor_cnpj || null,
          fornecedor_original: vals.fornecedor || null,   // pessoa (nome); id resolvido no backend
          fornecedor_id: reembolsoSelecionado.ai_extracao?.fornecedor_id || null,
          descricao: vals.descricao,
          categoria_dre: vals.categoria_dre || null,
          comentario_financeiro: vals.comentario_financeiro || null,
          ...payloadDestino(vals.destino),
        },
        { headers: { Authorization: token } },
      );
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(`Aprovado. Lançamento AP criado: ${data.lancamento_pagar_id}`);
      setModalAberto(false);
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally {
      setSalvando(false);
    }
  }

  // Editar depois de aprovado (Lucas 2026-07-08): salva no registro e
  // atualiza o lançamento do CP quando ele é exclusivo deste reembolso
  // (lançamento consolidado de grupo não é tocado — o backend avisa).
  async function salvarEdicaoAprovado() {
    if (!reembolsoSelecionado) return;
    let vals: any;
    try { vals = await form.validateFields(); }
    catch { message.error('Preencha os campos obrigatórios (valor, data de emissão e categoria).'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const dataIso = vals.data_emissao && (vals.data_emissao as Dayjs).isValid?.()
        ? (vals.data_emissao as Dayjs).format('YYYY-MM-DD')
        : null;
      const r = await axios.post(
        `${API_URL}/mobile/reembolsos/${reembolsoSelecionado.id}/editar-aprovado`,
        {
          valor: vals.valor,
          data_emissao: dataIso,
          emissor: vals.emissor || null,
          emissor_cnpj: vals.emissor_cnpj || null,
          fornecedor: vals.fornecedor || null,
          fornecedor_id: reembolsoSelecionado.ai_extracao?.fornecedor_id || null,
          descricao: vals.descricao,
          categoria_dre: vals.categoria_dre || null,
          comentario_financeiro: vals.comentario_financeiro || null,
          ...payloadDestino(vals.destino),
        },
        { headers: { Authorization: token } },
      );
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (data.aviso) message.warning(data.aviso, 7);
      message.success(data.lancamento_atualizado
        ? 'Alterações salvas — lançamento no Contas a Pagar atualizado junto'
        : 'Alterações salvas no reembolso');
      setModalAberto(false);
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally {
      setSalvando(false);
    }
  }

  async function rejeitar() {
    if (!reembolsoSelecionado) return;
    const comentario = form.getFieldValue('comentario_financeiro') || '';
    Modal.confirm({
      title: 'Rejeitar reembolso',
      content: (
        <div>
          <Text style={{ display: 'block', marginBottom: 8 }}>
            Motivo da rejeição (visível pra equipe):
          </Text>
          <Input.TextArea
            id="motivo-rejeicao"
            placeholder="Ex: foto borrada, documento ilegível, etc."
            rows={3}
            defaultValue={comentario}
          />
        </div>
      ),
      okText: 'Rejeitar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        const motivo = (document.getElementById('motivo-rejeicao') as HTMLTextAreaElement)?.value || '';
        try {
          const token = await getToken();
          await axios.post(
            `${API_URL}/mobile/reembolsos/${reembolsoSelecionado.id}/rejeitar`,
            { motivo, comentario_financeiro: motivo },
            { headers: { Authorization: token } },
          );
          message.success('Reembolso rejeitado');
          setModalAberto(false);
          carregar();
        } catch (e: any) {
          message.error('Falha: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  const columns = [
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      render: (v: string) => new Date(v).toLocaleDateString('pt-BR'),
    },
    {
      title: 'Usuário',
      dataIndex: 'email_usuario',
      key: 'email_usuario',
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Fornecedor',
      key: 'fornecedor',
      render: (_: any, r: Reembolso) => r.ai_extracao?.fornecedor || <Text type="secondary">{r.email_usuario}</Text>,
    },
    {
      title: 'Emissor (loja)',
      key: 'emissor',
      render: (_: any, r: Reembolso) => r.ai_extracao?.emissor || r.ai_extracao?.fornecedor || <Text type="secondary">—</Text>,
    },
    {
      title: 'Valor',
      key: 'valor',
      align: 'right' as const,
      width: 120,
      render: (_: any, r: Reembolso) => <strong>{fmtMoeda(r.ai_extracao?.valor ?? null)}</strong>,
    },
    {
      title: 'Centro de custo',
      key: 'contrato',
      render: (_: any, r: Reembolso) => r.contrato_numero || r.centro_custo_nome
        || <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (s: StatusReembolso) => {
        const tag = STATUS_TAG[s] || { color: 'default', txt: s };
        return <Tag color={tag.color}>{tag.txt}</Tag>;
      },
    },
    {
      title: '',
      key: 'avisos',
      width: 120,
      render: (_: any, r: Reembolso) => (
        r.duplicidade_de && r.status === 'aguardando_revisao'
          ? <Tag color="volcano">repetido do #{r.duplicidade_de}?</Tag>
          : r.ai_extracao?.erro_leitura && r.status === 'aguardando_revisao'
            ? <Tag>preencher à mão</Tag>
            : null
      ),
    },
    {
      title: '',
      key: 'acoes',
      width: 100,
      render: (_: any, r: Reembolso) => (
        <Button size="small" type="link" onClick={() => abrirRevisao(r)}>
          Revisar
        </Button>
      ),
    },
  ];

  return (
    <Card style={{ margin: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Title level={3} style={{ margin: 0 }}>📱 Reembolsos Campo</Title>
          <Space wrap>
            <Segmented
              value={filtro}
              onChange={(v) => {
                setPagina(1);
                setFiltro(v as string);
              }}
              options={[
                { label: 'Aguardando revisão', value: 'aguardando_revisao' },
                { label: 'Aprovados', value: 'aprovado' },
                { label: 'Rejeitados', value: 'rejeitado' },
                ...(comProblema > 0 || filtro === 'com_problema'
                  ? [{ label: `Com problema (${comProblema})`, value: 'com_problema' }] : []),
                { label: 'Todos', value: 'todos' },
              ]}
            />
            <Switch
              checkedChildren="Excluídos"
              unCheckedChildren="Excluídos"
              checked={mostrarExcluidos}
              onChange={(value) => {
                setPagina(1);
                setMostrarExcluidos(value);
              }}
            />
            <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>
              Atualizar
            </Button>
          </Space>
        </div>

        <Input.Search
          allowClear
          maxLength={100}
          value={busca}
          onChange={(event) => {
            setPagina(1);
            setBusca(event.target.value);
          }}
          onSearch={(texto) => {
            setPagina(1);
            setBusca(texto);
            setBuscaAplicada(texto.trim());
          }}
          placeholder="Buscar por fornecedor, solicitante, categoria ou contrato"
          aria-label="Buscar reembolsos"
          style={{ width: '100%', maxWidth: 560 }}
        />

        {erro && <Alert type="error" message={erro} />}
        {filtro === 'com_problema' && (
          <Alert
            type="warning" showIcon
            message="Pedidos antigos que ficaram presos sem ninguém ser avisado"
            description="A leitura automática falhou ou o sistema achou que era repetido. Antes de aprovar, confira se a pessoa não reenviou o mesmo comprovante (parte deles foi reenviada e paga em seguida). O que já foi pago por outro pedido: rejeite ou exclua."
          />
        )}

        <Table
          rowKey="id"
          dataSource={reembolsos}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagina,
            pageSize: ITENS_POR_PAGINA,
            showSizeChanger: false,
            onChange: (page) => setPagina(page),
          }}
          locale={{ emptyText: 'Sem reembolsos neste filtro' }}
        />
      </Space>

      <Modal
        open={modalAberto}
        onCancel={() => setModalAberto(false)}
        title={reembolsoSelecionado ? `Reembolso #${reembolsoSelecionado.id}` : ''}
        width={900}
        footer={reembolsoSelecionado?.excluido_em ? [
          <Button key="cancel" onClick={() => setModalAberto(false)}>Fechar</Button>,
          <Button key="restore" icon={<UndoOutlined />} onClick={desfazerExclusaoDre}>
            Desfazer exclusão
          </Button>,
        ] : reembolsoSelecionado?.status === 'aprovado' ? [
          <Button key="cancel" onClick={() => setModalAberto(false)}>Cancelar</Button>,
          <Button
            key="save-edit"
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={salvando}
            onClick={salvarEdicaoAprovado}
          >
            Salvar alterações (já aprovado)
          </Button>,
        ] : [
          <Button key="cancel" onClick={() => setModalAberto(false)}>Cancelar</Button>,
          <Button key="delete" danger icon={<DeleteOutlined />} onClick={excluir}>
            Excluir
          </Button>,
          <Button key="reject" danger icon={<CloseCircleOutlined />} onClick={rejeitar}>
            Rejeitar
          </Button>,
          <Button
            key="approve"
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={salvando}
            onClick={aprovar}
          >
            Aprovar e lançar em Contas a Pagar
          </Button>,
        ]}
      >
        {reembolsoSelecionado?.excluido_em && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Reembolso excluído"
            description={
              <span>
                Excluído em {new Date(reembolsoSelecionado.excluido_em).toLocaleString('pt-BR')}
                {reembolsoSelecionado.excluido_por && <> por {reembolsoSelecionado.excluido_por}</>}.
                {reembolsoSelecionado.motivo_exclusao && (
                  <><br/><em>Motivo:</em> {reembolsoSelecionado.motivo_exclusao}</>
                )}
              </span>
            }
          />
        )}
        {reembolsoSelecionado?.duplicidade_de && reembolsoSelecionado.status !== 'aprovado' && (
          <Alert
            type="warning" showIcon style={{ marginBottom: 12 }}
            message={`Pode ser repetido do pedido #${reembolsoSelecionado.duplicidade_de}`}
            description="Mesmo CNPJ, valor e data. Confira os dois antes de aprovar — se for o mesmo gasto, rejeite este."
          />
        )}
        {reembolsoSelecionado?.ai_extracao?.erro_leitura && reembolsoSelecionado.status !== 'aprovado' && (
          <Alert
            type="info" showIcon style={{ marginBottom: 12 }}
            message="A leitura automática não funcionou neste comprovante"
            description="Preencha valor, data e emissor olhando para a imagem."
          />
        )}
        {reembolsoSelecionado?.sem_comprovante && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Lançamento sem comprovante"
            description={
              reembolsoSelecionado.justificativa_sem_comprovante
                ? `Justificativa: ${reembolsoSelecionado.justificativa_sem_comprovante}`
                : 'Sem justificativa informada'
            }
          />
        )}
        {/* O que o SOLICITANTE disse que é. Vem antes de tudo porque é o
            contexto que a nota fiscal não tem — "POSTO X" não diz pra que
            serviu. Some quando ninguém escreveu (reembolso antigo). */}
        {reembolsoSelecionado?.descricao_solicitante && (
          <Alert
            type="info" showIcon style={{ marginBottom: 12 }}
            message={`A que se refere — ${reembolsoSelecionado.email_usuario}`}
            description={reembolsoSelecionado.descricao_solicitante}
          />
        )}
        {reembolsoSelecionado && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              {fotoUrl ? (
                <>
                  {/* Comprovante em PDF não renderiza em <img> — a revisora via um
                      quadro vazio e não tinha como conferir (relato da Daniele,
                      19/08/2026). PDF vai em <iframe> (o objeto no S3 tem
                      Content-Type application/pdf, então o navegador desenha). */}
                  {ehPdf ? (
                    <iframe
                      src={fotoUrl}
                      title="comprovante em PDF"
                      style={{ width: '100%', height: 500, border: '1px solid #eee', background: '#fafafa' }}
                    />
                  ) : (
                    <Image src={fotoUrl} alt="comprovante" style={{ maxWidth: '100%', maxHeight: 500 }} />
                  )}
                  <Space style={{ marginTop: 8 }} wrap>
                    <Button size="small" icon={<DownloadOutlined />}
                      onClick={() => {
                        const url = fotoDownloadUrl || fotoUrl;
                        if (!url) return;
                        // O Content-Disposition: attachment da URL força o download
                        // do arquivo em qualquer navegador (Chrome, Edge).
                        const a = document.createElement('a');
                        a.href = url; a.rel = 'noopener';
                        document.body.appendChild(a); a.click(); a.remove();
                      }}>
                      Baixar {ehPdf ? 'PDF' : 'foto'}
                    </Button>
                    {/* Escape hatch: alguns PDFs (assinados, com fontes exóticas)
                        não desenham dentro do iframe; abrir em aba resolve. */}
                    <Button size="small" onClick={() => window.open(fotoUrl, '_blank', 'noopener')}>
                      Abrir em nova aba
                    </Button>
                  </Space>
                </>
              ) : (
                <Space direction="vertical" size={4}>
                  <Text type="secondary">Carregando comprovante…</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Se não aparecer, o link pode ter expirado — feche e abra o pedido de novo.
                  </Text>
                </Space>
              )}
              <Descriptions size="small" column={1} style={{ marginTop: 12 }}>
                <Descriptions.Item label="Usuário">{reembolsoSelecionado.email_usuario}</Descriptions.Item>
                <Descriptions.Item label="Capturado em">
                  {new Date(reembolsoSelecionado.capturado_em).toLocaleString('pt-BR')}
                </Descriptions.Item>
                <Descriptions.Item label="Centro de custo">
                  {reembolsoSelecionado.contrato_numero
                    ? `${reembolsoSelecionado.contrato_numero} ${reembolsoSelecionado.contrato_titulo}`
                    : reembolsoSelecionado.centro_custo_nome || '— sem centro de custo —'}
                </Descriptions.Item>
                <Descriptions.Item label="Confiança IA">
                  {reembolsoSelecionado.ai_extracao?.confianca != null
                    ? `${Math.round(reembolsoSelecionado.ai_extracao.confianca * 100)}%`
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo doc">
                  {reembolsoSelecionado.ai_extracao?.tipo_documento || '—'}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div>
              <Form form={form} layout="vertical">
                <Form.Item label="Valor (R$)" name="valor" rules={[{ required: true, message: 'obrigatório' }]}>
                  <InputNumber<number>
                    style={{ width: '100%' }}
                    min={0}
                    step={0.01}
                    decimalSeparator=","
                    precision={2}
                  />
                </Form.Item>
                <Form.Item
                  label="Data emissão"
                  name="data_emissao"
                  rules={[{ required: true, message: 'obrigatório' }]}
                  // Esta data vira o mês da despesa na DRE E o vencimento no Contas
                  // a Pagar — e é o que a conciliação bancária usa pra achar o
                  // lançamento (janela de ±60 dias da transação). Por isso a foto
                  // aparece aqui do lado: é a testemunha mais barata que existe.
                  extra={reembolsoSelecionado
                    ? `${reembolsoSelecionado.sem_comprovante ? 'Registrado' : 'Comprovante fotografado'} em `
                      + dayjs(reembolsoSelecionado.capturado_em).format('DD/MM/YYYY')
                    : undefined}
                >
                  <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="dd/mm/aaaa" />
                </Form.Item>
                {alertaData && (
                  <Alert
                    type={alertaData.confirmado_por ? 'info' : 'warning'}
                    showIcon
                    style={{ marginTop: -8, marginBottom: 16 }}
                    message={alertaData.confirmado_por ? 'Data fora do normal, já conferida' : 'Confira a data no comprovante'}
                    description={
                      <Space direction="vertical" size={4}>
                        <span>{alertaData.motivo}.</span>
                        {alertaData.confirmado_por && (
                          <span style={{ fontSize: 12 }}>
                            Conferida por {alertaData.confirmado_por}
                            {alertaData.confirmado_em
                              ? ` em ${dayjs(alertaData.confirmado_em).format('DD/MM/YYYY')}` : ''}.
                          </span>
                        )}
                        {/* Só existe quando a data NÃO veio do texto do documento:
                            se ela veio, "dia e mês trocados" seria invenção. */}
                        {alertaData.data_sugerida && (
                          <Space wrap size={6}>
                            <span>
                              Parece {alertaData.sugestao_motivo}:{' '}
                              <b>{dayjs(alertaData.data_sugerida).format('DD/MM/YYYY')}</b>.
                            </span>
                            <Button
                              size="small"
                              htmlType="button"
                              onClick={() => form.setFieldsValue({
                                data_emissao: dayjs(alertaData.data_sugerida as string),
                              })}
                            >
                              Usar {dayjs(alertaData.data_sugerida).format('DD/MM/YYYY')}
                            </Button>
                          </Space>
                        )}
                      </Space>
                    }
                  />
                )}
                <Form.Item
                  label="Fornecedor (quem recebe o reembolso)"
                  name="fornecedor"
                  tooltip="A pessoa que pediu o reembolso. Em branco = usa o solicitante e tenta vincular ao cadastro de fornecedores."
                  extra={reembolsoSelecionado ? `Solicitante: ${reembolsoSelecionado.email_usuario}` : undefined}
                >
                  <Input placeholder="Nome da pessoa (em branco = usa o solicitante)" />
                </Form.Item>
                <Form.Item
                  label="Centro de custo"
                  name="destino"
                  tooltip="Escolha o contrato ou centro indireto para a despesa em Contas a Pagar."
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Selecione contrato ou centro indireto"
                    options={[
                      { label: 'Contratos', options: contratos.map((c) => ({
                        value: `contrato:${c.id}`, label: `${c.numero || 's/nº'} ${c.titulo || ''}`.trim(),
                      })) },
                      { label: 'Centros indiretos', options: centrosCusto.map((c) => ({
                        value: `centro:${c.codigo_canonical}`,
                        label: `${c.nome_completo} (${c.codigo_canonical})`,
                      })) },
                    ]}
                  />
                </Form.Item>
                <Form.Item label="Emissor (loja / estabelecimento do documento)" name="emissor">
                  <Input placeholder="Nome da loja onde a compra foi feita" />
                </Form.Item>
                <Form.Item label="CNPJ do emissor" name="emissor_cnpj">
                  <Input placeholder="00.000.000/0000-00" />
                </Form.Item>
                <Form.Item
                  label="Categoria DRE (vai pro lançamento em Contas a Pagar)"
                  name="categoria_dre"
                  tooltip="Categoria oficial do DRE. Sugestão pré-preenchida pela IA quando disponível."
                  rules={[{ required: true, message: 'selecione uma categoria' }]}
                >
                  <Select
                    options={categorias.map((c) => ({
                      label: c.linha_dre,
                      value: c.linha_dre,
                      group: c.grupo_dre,
                    }))}
                    allowClear
                    placeholder="Selecionar categoria DRE…"
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
                <Form.Item label="Descrição" name="descricao">
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item
                  label="Comentário pro colaborador"
                  name="comentario_financeiro"
                  tooltip="Mensagem visível pro funcionário no PWA (opcional)"
                >
                  <Input.TextArea rows={2} placeholder="Ex: aprovado conforme política. / Tirar nova foto da nota fiscal." />
                </Form.Item>
              </Form>
            </div>
          </div>
        )}

        {eventos.length > 0 && (
          <Card size="small" title="📜 Histórico" style={{ marginTop: 16 }}>
            <Timeline
              items={eventos.map((ev: any) => {
                const meta = EVENTO_LABEL[ev.evento] || { color: 'gray', txt: ev.evento };
                const motivo = ev.detalhes?.motivo || ev.detalhes?.comentario;
                const just = ev.detalhes?.justificativa;
                return {
                  color: meta.color,
                  children: (
                    <div>
                      <strong>{meta.txt}</strong>
                      <div style={{ fontSize: 11, color: '#888' }}>
                        {new Date(ev.created_at).toLocaleString('pt-BR')}
                        {ev.ator_email && <> · {ev.ator_email}</>}
                        {ev.ator_role && ev.ator_role !== 'campo' && <> ({ev.ator_role})</>}
                      </div>
                      {motivo && <div style={{ fontSize: 12, fontStyle: 'italic', marginTop: 4 }}>"{motivo}"</div>}
                      {just && <div style={{ fontSize: 12, marginTop: 4, color: '#d97706' }}><strong>Sem comprovante:</strong> {just}</div>}
                    </div>
                  ),
                };
              })}
            />
          </Card>
        )}
      </Modal>
    </Card>
  );
}

export default function FinanceiroReembolsosCampo() {
  return (
    <AntApp>
      <FinanceiroReembolsosCampoInner />
    </AntApp>
  );
}
